import { Search, ArrowLeftRight, Crosshair, History, Map, Lightbulb, Check } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { SectionHead } from "@/components/SectionHead";
import { CtaStrip } from "@/components/CtaStrip";

const ACTIONS = [
  {
    icon: Search,
    title: "Search Any Constituency",
    body: "Instantly see candidates, votes, vote share, margin and status for any constituency.",
  },
  {
    icon: ArrowLeftRight,
    title: "Compare Performance",
    body: "Compare party performance across states, districts or election cycles.",
  },
  {
    icon: Crosshair,
    title: "Find the Story",
    body: "Identify the closest contests, biggest upsets and constituency turnovers.",
  },
  {
    icon: History,
    title: "Add Historical Context",
    body: "Access historical data to add context to current results.",
  },
  {
    icon: Map,
    title: "Explore Geography",
    body: "Use interactive maps to explore geographic patterns in party support.",
  },
  {
    icon: Lightbulb,
    title: "Surface Leads",
    body: "Find data-backed leads for political reporting.",
  },
];

const ANALYST_POINTS = [
  "Constituency-level historical analysis across elections",
  "Vote-share and seat-share comparisons with transparent methodology",
  "Margin and competitiveness analysis",
  "Geographic concentration of party support",
  "Candidate performance tracking across elections and constituencies",
  "Data exploration tools for testing research hypotheses",
];

const ForJournalists = () => (
  <div data-testid="for-journalists-page">
    <Seo
      title="For Journalists & Analysts — Election Data That Saves You Hours | Anlytx for Elections"
      description="Structured constituency data, historical comparisons, visual result exploration and analytical leads for journalists and political analysts."
    />

    <section className="relative overflow-hidden bg-[#0b1322]" data-testid="journalists-hero">
      <div className="absolute inset-0 grid-faint-dark opacity-70" />
      <div className="absolute -top-32 right-1/4 h-80 w-[60%] rounded-full bg-[#b91c1c]/15 blur-3xl" />
      <div className="relative mx-auto max-w-4xl px-6 py-20 text-center sm:py-28">
        <Reveal>
          <p className="font-mono-data text-xs font-medium uppercase tracking-[0.24em] text-red-400">
            For Journalists &amp; Analysts
          </p>
          <h1 className="mt-4 font-heading text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
            Election Data That Saves You Hours
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
            Structured constituency data, historical comparisons, visual result exploration and analytical leads — so
            you can move from raw results to an evidence-based story faster.
          </p>
        </Reveal>
      </div>
    </section>

    <section className="bg-white" data-testid="journalists-actions">
      <div className="mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <SectionHead eyebrow="What You Can Do" title="From Deadline Pressure to Published Story" />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ACTIONS.map((a, i) => (
            <Reveal key={a.title} delay={(i % 3) * 0.1}>
              <div
                data-testid={`action-${a.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="h-full rounded-xl border border-slate-200 bg-white p-7 transition-all duration-300 hover:-translate-y-1 hover:border-[#b91c1c]/40 hover:shadow-[0_20px_50px_-24px_rgba(185,28,28,0.22)]"
              >
                <span className="flex h-10 w-10 items-center justify-center rounded-md bg-[#fef2f2]">
                  <a.icon className="h-5 w-5 text-[#b91c1c]" />
                </span>
                <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">{a.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{a.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>

    <section className="relative overflow-hidden bg-[#f8fafc]" data-testid="journalists-analysts">
      <div className="absolute inset-0 dot-grid opacity-30" />
      <div className="relative mx-auto max-w-7xl px-6 py-20 sm:py-24">
        <div className="grid gap-12 lg:grid-cols-[0.8fr_1fr]">
          <SectionHead
            align="left"
            eyebrow="Deeper Work"
            title="For Political Analysts & Researchers"
            sub="The same structured data layer, extended for sustained research — with every metric traceable to a documented definition."
          />
          <div className="space-y-3">
            {ANALYST_POINTS.map((p, i) => (
              <Reveal key={p} delay={i * 0.06}>
                <div
                  className="flex items-start gap-3 rounded-lg border border-slate-200 bg-white p-4"
                  data-testid={`analyst-point-${i + 1}`}
                >
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#fef2f2]">
                    <Check className="h-3 w-3 text-[#b91c1c]" />
                  </span>
                  <p className="text-sm font-medium leading-relaxed text-slate-700">{p}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </section>

    <CtaStrip
      testid="journalists-cta-strip"
      heading="Spend Election Results Day Writing, Not Wrangling Spreadsheets"
      subheading="Request analyst access, or tell us the data requirement your newsroom or research project needs."
      primary={{ label: "Request Analyst Access", to: "/contact?type=analyst" }}
      secondary={{ label: "Discuss a Data Requirement", to: "/contact?type=data" }}
    />
  </div>
);

export default ForJournalists;
